/**
 * Single source of truth for public, indexable routes.
 * Used by sitemap.xml generation and the human-readable /sitemap page.
 */

export type ChangeFreq =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never';

export interface SitemapEntry {
  path: string;
  /** Optional label for the HTML sitemap page */
  label?: string;
  /** Sitemap priority 0.0–1.0 */
  priority?: number;
  changefreq?: ChangeFreq;
  /** Group key for grouping on the HTML sitemap (e.g. 'main', 'legal', 'info') */
  group?: string;
}

/** Base URL for canonical and sitemap URLs. Set via VITE_APP_BASE_URL at build time. */
export const getBaseUrl = (): string =>
  (import.meta.env.VITE_APP_BASE_URL as string) || 'https://uat.hirepanther.ca';

export const SITEMAP_ENTRIES: SitemapEntry[] = [
  // Main
  {
    path: '/home',
    label: 'Home',
    priority: 1.0,
    changefreq: 'daily',
    group: 'main',
  },
  {
    path: '/services/service-grid',
    label: 'Services',
    priority: 0.9,
    changefreq: 'daily',
    group: 'main',
  },
  {
    path: '/services/service-list',
    label: 'Service List',
    priority: 0.9,
    changefreq: 'daily',
    group: 'main',
  },
  {
    path: '/services/search',
    label: 'Search Services',
    priority: 0.8,
    changefreq: 'daily',
    group: 'main',
  },
  {
    path: '/services/search-list',
    label: 'Service List',
    priority: 0.9,
    changefreq: 'daily',
    group: 'main',
  },
  {
    path: '/registration-provider',
    label: 'Become a Provider',
    priority: 0.7,
    changefreq: 'monthly',
    group: 'main',
  },
  {
    path: '/support-ticket',
    label: 'Support',
    priority: 0.6,
    changefreq: 'monthly',
    group: 'main',
  },

  // Info & how-to
  {
    path: '/how-it-works',
    label: 'How It Works',
    priority: 0.8,
    changefreq: 'monthly',
    group: 'info',
  },
  {
    path: '/about-hirepanther',
    label: 'About Us',
    priority: 0.7,
    changefreq: 'monthly',
    group: 'info',
  },
  {
    path: '/contact',
    label: 'Contact',
    priority: 0.8,
    changefreq: 'monthly',
    group: 'info',
  },
  {
    path: '/safety-insurance',
    label: 'Safety & Insurance',
    priority: 0.6,
    changefreq: 'monthly',
    group: 'info',
  },
  {
    path: '/report-issue',
    label: 'Report an Issue',
    priority: 0.5,
    changefreq: 'monthly',
    group: 'info',
  },
  {
    path: '/accessibility',
    label: 'Accessibility',
    priority: 0.5,
    changefreq: 'monthly',
    group: 'info',
  },

  // Legal & policies
  {
    path: '/terms-services',
    label: 'Terms of Service',
    priority: 0.5,
    changefreq: 'yearly',
    group: 'legal',
  },
  {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    priority: 0.5,
    changefreq: 'yearly',
    group: 'legal',
  },
  {
    path: '/cookie-policy',
    label: 'Cookie Policy',
    priority: 0.4,
    changefreq: 'yearly',
    group: 'legal',
  },
  {
    path: '/tax-policy',
    label: 'Tax Policy',
    priority: 0.4,
    changefreq: 'yearly',
    group: 'legal',
  },
  {
    path: '/advertising-policy',
    label: 'Advertising Policy',
    priority: 0.4,
    changefreq: 'yearly',
    group: 'legal',
  },
  {
    path: '/dmca',
    label: 'DMCA',
    priority: 0.4,
    changefreq: 'yearly',
    group: 'legal',
  },

  // Discovery
  {
    path: '/sitemap',
    label: 'Sitemap',
    priority: 0.4,
    changefreq: 'weekly',
    group: 'discovery',
  },
];
